import { format as dateFormat } from "date-fns"
import humanizeDuration from "humanize-duration"
import { Job, getJobId as getJobIdFromJob } from "./job"

export interface JobInvoiceItemKey {
  /** The User ID, formatted as USER#<UserId> */
  pk: string

  /** The Job Invoice Item (scoped to the user and yearmonth), formatted as JOBII#yyyyMM#<JobId> */
  sk: string
}

export interface JobInvoiceItem extends JobInvoiceItemKey {
  billable_seconds: number
  total_seconds: number

  /** ISO8601 datetime of when the Job this is associated with was submitted at */
  submitted_at: string
  job_name: string
}

const nearestMinute = (secs: number) => Math.ceil(secs / 60) * 60

export const createJobInvoiceItem = (job: Job, freeTrialSecondsRemaining: number) => {
  if (!job.file_metadata) {
    // we can't calculate this file because it hasn't been analyzed yet
    return null
  }

  const yearmonth = dateFormat(new Date(job.submitted_at), 'yyyyMM')
  const total_seconds = nearestMinute(parseFloat(job.file_metadata.format.duration))

  const jii: JobInvoiceItem = {
    pk: job.pk, // pk is identical
    sk: `JOBII#${yearmonth}#${getJobIdFromJob(job)}`,
    billable_seconds: Math.max(total_seconds - freeTrialSecondsRemaining, 0),
    total_seconds,
    submitted_at: job.submitted_at,
    job_name: job.job_name,
  }

  return jii
}

export const calculateTotalSeconds = (invoiceItems: JobInvoiceItem[]) => {
  return invoiceItems.map((jii) => jii.total_seconds).reduce((t, s) => (t + s), 0)
}

export const calculateTotalBillableSeconds = (invoiceItems: JobInvoiceItem[]) => {
  return invoiceItems.map((jii) => jii.billable_seconds).reduce((t, s) => (t + s), 0)
}

export const formatDuration = (durationSeconds: number) => {
  return humanizeDuration(durationSeconds * 1000, { units: ["h", "m"], delimiter: " and " })
}

/**
 * calculateBillableAmount
 *
 * returns cents, not dollars
 *
 * @param invoiceItem the invoice item
 */
export const calculateBillableAmount = (invoiceItem: JobInvoiceItem) => {
  const durationMinutes = Math.ceil(invoiceItem.billable_seconds / 60)
  return durationMinutes * 5 // 5 cents per minute
}

/**
 * calculateTotalBillableAmount
 *
 * returns the total amount billable (that is, excluding free trial minutes) in USD cents.
 */
export const calculateTotalBillableAmount = (invoiceItems: JobInvoiceItem[]) => {
  return invoiceItems.reduce((t, i) => (t + calculateBillableAmount(i)), 0)
}

export const getJobId = (jii: JobInvoiceItemKey) => jii.sk.split('#')[2]
